import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Card, Column, Row, TextButton, Typography, CloseButton } from 'seagull';
import { LogoPicker } from '~/components/LogoPicker/LogoPicker';
import { useLocaleKeys } from '~/i18n';
import { useBrandingActions } from '../BrandingActionsContext';
import { PreviewState } from '../ClientOnboarding.utils';
import { useCobBranding } from '../CobBrandingContext';
import { useCobConfig } from '../CobConfigContext';
import styles from './BrandingControllers.module.scss';
import { ColorsLabel, SectionTitle } from './BrandingControllers.shared';
import { ColorSelectorWithPopup } from './ColorSelectorWithPopup';
export function BrandingPanel() {
    const { branding, logoImage, errors, isDirty, isValid, setBranding, setLogo, revertToDefault, saveLogoAndBranding } = useCobBranding();
    const { t: texts } = useLocaleKeys('clientFlow.brandingPanel');
    const { originalPreviewState } = useCobConfig();
    const { onToggleBrandSetup } = useBrandingActions();
    function handleCloseClick() {
        onToggleBrandSetup();
    }
    function getColorControl(colorKey) {
        return /*#__PURE__*/ _jsx(ColorSelectorWithPopup, {
            branding: branding,
            hasError: errors.has(colorKey),
            colorKey: colorKey,
            onChange: setBranding
        });
    }
    return /*#__PURE__*/ _jsxs(Card, {
        padding: "s",
        className: styles.brandingCard,
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                alignItems: "center",
                justifyContent: "space-between",
                className: styles.headerRow,
                children: [
                    /*#__PURE__*/ _jsx(SectionTitle, {
                        variant: "paragraph1",
                        children: texts.customizeBrand()
                    }),
                    originalPreviewState !== PreviewState.SetupBrand && /*#__PURE__*/ _jsx(CloseButton, {
                        skin: "standard",
                        onClick: handleCloseClick,
                        testId: "close-branding-setup-btn"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Column, {
                gap: 12,
                className: styles.scrollableContent,
                children: [
                    /*#__PURE__*/ _jsx(LogoPicker, {
                        isNewUI: true,
                        testId: "upload-logo",
                        image: logoImage,
                        onChange: setLogo
                    }),
                    /*#__PURE__*/ _jsx(SectionTitle, {
                        children: texts.chooseBrandColors()
                    }),
                    /*#__PURE__*/ _jsxs(Column, {
                        gap: 8,
                        children: [
                            /*#__PURE__*/ _jsxs(Row, {
                                alignItems: "center",
                                gap: 8,
                                children: [
                                    /*#__PURE__*/ _jsx(ColorsLabel, {
                                        skin: "secondary",
                                        children: texts.text()
                                    }),
                                    /*#__PURE__*/ _jsxs(Row, {
                                        gap: 6,
                                        children: [
                                            getColorControl('textStandard'),
                                            getColorControl('textSecondary')
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Row, {
                                alignItems: "center",
                                gap: 8,
                                children: [
                                    /*#__PURE__*/ _jsx(ColorsLabel, {
                                        skin: "secondary",
                                        children: texts.buttons()
                                    }),
                                    /*#__PURE__*/ _jsxs(Row, {
                                        gap: 6,
                                        children: [
                                            getColorControl('buttonPrimary'),
                                            getColorControl('buttonSecondary'),
                                            getColorControl('buttonDisabled')
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Row, {
                                alignItems: "center",
                                gap: 8,
                                children: [
                                    /*#__PURE__*/ _jsx(ColorsLabel, {
                                        skin: "secondary",
                                        children: texts.background()
                                    }),
                                    /*#__PURE__*/ _jsxs(Row, {
                                        gap: 6,
                                        children: [
                                            getColorControl('backgroundGradientStart'),
                                            getColorControl('backgroundGradientEnd'),
                                            getColorControl('backgroundLight'),
                                            getColorControl('backgroundStandard')
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Row, {
                                alignItems: "center",
                                gap: 8,
                                children: [
                                    /*#__PURE__*/ _jsx(ColorsLabel, {
                                        skin: "secondary",
                                        children: texts.progressBar()
                                    }),
                                    /*#__PURE__*/ _jsxs(Row, {
                                        gap: 6,
                                        children: [
                                            getColorControl('progressBarComplete'),
                                            getColorControl('progressBarIncomplete')
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(TextButton, {
                        testId: "undo-color-changes",
                        underline: "none",
                        onClick: revertToDefault,
                        children: texts.revertToDefaultColors()
                    }),
                    /*#__PURE__*/ _jsxs(Column, {
                        gap: 10,
                        alignItems: "center",
                        className: styles.saveSection,
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                skin: "primaryOutline",
                                disabled: !isDirty || !isValid,
                                testId: "save-colors",
                                tooltip: {
                                    placement: 'top',
                                    title: !isDirty ? texts.nothingToSave() : !isValid ? texts.oneOfTheFieldsIsInvalid() : ''
                                },
                                onClick: saveLogoAndBranding,
                                className: styles.saveBtn,
                                children: texts.saveChanges()
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                skin: "secondary",
                                className: styles.saveChangesInfo,
                                children: texts.changesSavedInfo()
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
